/**
 * Cache utility for storing API responses in IndexedDB.
 * Features:
 *  - Lazy demand (stale-while-revalidate)
 *  - Server-side fingerprint validation (checksum-based invalidation)
 */

const DB_NAME = 'VECTCache';
const STORE_NAME = 'dropdowns';
const DB_VERSION = 1;

const BUILD_FINGERPRINT = process.env.VUE_APP_BUILD_FINGERPRINT || 'dev';
const STORAGE_FP_KEY = 'vect_build_fingerprint';

let dbPromise = null;

const checkAndClearOnNewBuild = async () => {
    try {
        const storedFp = localStorage.getItem(STORAGE_FP_KEY);
        if (!storedFp || storedFp !== BUILD_FINGERPRINT) {
            localStorage.setItem(STORAGE_FP_KEY, BUILD_FINGERPRINT);
        }
    } catch (e) {
        console.warn('[Cache] Failed to verify build fingerprint:', e);
    }
};

const getDB = () => {
    if (!dbPromise) {
        dbPromise = (async () => {
            await checkAndClearOnNewBuild();
            return new Promise((resolve, reject) => {
                const request = indexedDB.open(DB_NAME, DB_VERSION);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const db = request.result;
                    db.onversionchange = () => {
                        db.close();
                        dbPromise = null;
                        console.log('[IDB] Database closed due to versionchange in another tab.');
                    };
                    resolve(db);
                };
                request.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains(STORE_NAME)) {
                        db.createObjectStore(STORE_NAME);
                    }
                };
            });
        })();
    }
    return dbPromise;
};

export const setCache = async (key, data, ttlMs = 300000, fingerprint = null) => {
    try {
        const db = await getDB();
        const payload = {
            data: data,
            expiry: new Date().getTime() + ttlMs,
            fingerprint: fingerprint || null  // Store server fingerprint alongside data
        };
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.put(payload, key);
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.warn('Failed to set IndexedDB cache for', key, e);
    }
};

export const getCache = async (key) => {
    try {
        const db = await getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const request = store.get(key);
            request.onsuccess = () => {
                if (!request.result) {
                    resolve(null);
                    return;
                }
                const item = request.result;
                const now = new Date().getTime();

                // If expired, we can still return it for lazy loading, but mark as expired
                // Let the caller decide how to handle it.
                if (now > item.expiry) {
                    resolve({ data: item.data, isExpired: true, fingerprint: item.fingerprint });
                } else {
                    resolve({ data: item.data, isExpired: false, fingerprint: item.fingerprint });
                }
            };
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.warn('Failed to read IndexedDB cache for', key, e);
        return null;
    }
};

export const clearCache = async (prefix) => {
    try {
        const db = await getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.openCursor();
            
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    if (cursor.key.startsWith(prefix)) {
                        cursor.delete();
                    }
                    cursor.continue();
                } else {
                    resolve(true);
                }
            };
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.warn('Failed to clear IndexedDB cache with prefix', prefix, e);
    }
};

export const clearAllCache = async () => {
    try {
        const db = await getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.clear();
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.warn('Failed to clear entire IndexedDB cache', e);
    }
};

/**
 * Purge keys that match obsolete naming patterns.
 * Call once on app startup (e.g. in main.js) to silently clean up stale IDB entries
 * left over from previous naming conventions.
 *
 * Currently removes:
 *   - dropdown_index_*  (old format, replaced by dropdown_manifest_*)
 */
export const purgeObsoleteKeys = async () => {
    const OBSOLETE_PREFIXES = ['dropdown_index_'];
    try {
        const db = await getDB();
        return new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.openCursor();
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    const isObsolete = OBSOLETE_PREFIXES.some(p => cursor.key.startsWith(p));
                    if (isObsolete) {
                        cursor.delete();
                        console.debug('[IDB] Purged obsolete key:', cursor.key);
                    }
                    cursor.continue();
                } else {
                    resolve(true);
                }
            };
            request.onerror = () => resolve(false);
        });
    } catch (e) {
        console.warn('purgeObsoleteKeys failed:', e);
    }
};

/**
 * Validate all cached dropdown item fingerprints against the server fingerprints.
 * If there is a mismatch or a cache item is missing a fingerprint (and the server has one),
 * invalidate/delete that cached item from IndexedDB.
 */
export const validateCachedFingerprints = async (serverFingerprints) => {
    if (!serverFingerprints || typeof serverFingerprints !== 'object') return;
    try {
        const db = await getDB();
        return new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.openCursor();
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    const key = cursor.key;
                    if (key.startsWith('dropdown_item_')) {
                        const resourceKey = key.replace('dropdown_item_', '');
                        const cachedItem = cursor.value;
                        const serverFp = serverFingerprints[resourceKey];
                        
                        if (serverFp !== undefined) {
                            if (!cachedItem || cachedItem.fingerprint !== serverFp) {
                                cursor.delete();
                                console.debug(`[IDB] Invalidated cache for ${resourceKey} due to fingerprint mismatch. Cached: ${cachedItem?.fingerprint}, Server: ${serverFp}`);
                            }
                        }
                    }
                    cursor.continue();
                } else {
                    resolve(true);
                }
            };
            request.onerror = () => resolve(false);
        });
    } catch (e) {
        console.warn('validateCachedFingerprints failed:', e);
    }
};

/**
 * Invalidates any cached combinations (by prefix) that do not match the current valid fingerprint.
 * This actively deletes stale combinations instead of waiting for them to be requested.
 */
export const invalidateStaleCacheCombos = async (prefix, validFingerprint) => {
    try {
        const db = await getDB();
        return new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.openCursor();
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    if (cursor.key.startsWith(prefix)) {
                        const item = cursor.value;
                        if (item.fingerprint !== validFingerprint) {
                            cursor.delete();
                            console.debug(`[IDB] Invalidated stale cache combo for: ${cursor.key}`);
                        }
                    }
                    cursor.continue();
                } else {
                    resolve(true);
                }
            };
            request.onerror = () => resolve(false);
        });
    } catch (e) {
        console.warn('Failed to invalidate stale cache combos', e);
    }
};

/**
 * Check cache version first. If mismatched, fetch full fingerprints and validate/invalidate.
 */
/**
 * Returns milliseconds remaining until 23:59:59.999 of today (local time).
 * Used as the TTL for cached dropdown data so the cache always expires at
 * midnight, aligning with the backend's date-based fingerprint key.
 */
const msUntilEndOfDay = () => {
    const now = new Date();
    const endOfDay = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23, 59, 59, 999
    );
    return Math.max(endOfDay.getTime() - now.getTime(), 1000); // at least 1s
};

let validatePromise = null;

// Deduplicated in-flight promise for /dropdown-fingerprints within cacheService.
// Mirrors the same pattern used in api/index.js to prevent parallel calls.
let csFpPromise = null;
const fetchFP = async () => {
    if (csFpPromise) return csFpPromise;
    const { default: apiClient } = await import('@/services/http');
    csFpPromise = apiClient.get('/dropdown-fingerprints').finally(() => {
        setTimeout(() => { csFpPromise = null; }, 5000);
    });
    return csFpPromise;
};

export const checkAndValidateFingerprints = async () => {
    if (validatePromise) return validatePromise;
    validatePromise = (async () => {
        try {
            const { default: apiClient } = await import('@/services/http');
            const versionRes = await apiClient.get('/dropdown-fingerprints/version');
        const serverVersion = versionRes.data?.data?.version ?? versionRes.data?.version;
        
        const localVersion = localStorage.getItem('dropdown_fingerprints_version');
        if (localVersion && serverVersion && localVersion === serverVersion) {
            return; // matches, no need to sync
        }
        
        // Version changed, fetch ALL unified dropdowns
        const unifiedRes = await apiClient.get('/dropdown-fingerprints/all');
        const unifiedPayload = unifiedRes.data?.data ?? {};
        const unifiedData = unifiedPayload.data ?? {};
        const unifiedManifests = unifiedPayload.manifests ?? {};

        // Reuse the deduplicated FP fetch — avoids a redundant request
        const fpRes = await fetchFP();
        const serverFP = fpRes.data?.data ?? fpRes.data ?? {};

        // 1. Populate individual items — expire at end of today
        for (const [key, value] of Object.entries(unifiedData)) {
            const ttl = msUntilEndOfDay();
            const fp = serverFP[key] || serverVersion;
            await setCache(`dropdown_item_${key}`, value, ttl, fp);
        }

        // 2. Populate manifests — expire at end of today
        for (const [resource, keys] of Object.entries(unifiedManifests)) {
            const manifestKey = `dropdown_manifest_${resource}_default`;
            const fp = serverVersion;
            await setCache(manifestKey, keys, msUntilEndOfDay(), fp);
        }
        
        await validateCachedFingerprints(serverFP);
        
        if (serverVersion) {
            localStorage.setItem('dropdown_fingerprints_version', serverVersion);
        }
    } catch (e) {
        console.warn('checkAndValidateFingerprints failed:', e);
    } finally {
        // Keep the promise alive briefly so rapid re-callers share it,
        // then clear so the next page load can re-run the check.
        setTimeout(() => { validatePromise = null; }, 5000);
    }
    })();
    return validatePromise;
};
